import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ReplayIcon from '@mui/icons-material/Replay';
import ReactMarkdown from 'react-markdown';
import {
  syncDocumentsApi,
  SyncBranchSubject,
  SyncDistrictSubject,
  SyncDocument,
  SyncDocumentSummary,
  SyncPdfKind,
} from '../api/syncDocuments';
import { extractApiError } from '../utils/apiErrors';

// SYNC generator (sc-6314): pick a branch, optionally add context, Generate.
// A backend job runs the agentic loop; this page polls and renders the two
// markdown documents in tabs. Printing goes through the browser print
// stylesheet below; Download PDF fetches the server-rendered branded PDF of
// the active tab (sc-6344). "Make another sync" starts a fresh run; Revise
// (sc-6344) starts a child run that replays this one with the rep's feedback.
//
// v0.4 (sc-6408): a document with a `page` is the one-page format — the
// server's PDF inline with the rep's notes beside it, a Download button, and
// a warning when the page doesn't fit one sheet. The tabs, Print and the
// per-tab PDF stay for pre-v0.4 documents only.
//
// Page state (sc-6338): the URL search params (?location=&doc=) are the single
// source for the picked branch and the active run — a mid-run reload restores
// the running view and keeps polling; a pasted link opens the same doc.
//
// No branch picked (sc-6437): a Recent syncs card lists the newest completed
// syncs across branches (each revision chain as its latest revision); a click
// picks that branch and opens that doc, and the branch's own list takes over.
//
// Districts (sc-6439): one picker, "Branch or district", grouped Districts
// then Branches (GET /sync_documents/subjects). A district sets ?district=
// (its CRM id) and clears location and doc; its doc is a portrait page. An
// unknown ?district= or ?location= shows the empty picker.

const POLL_INTERVAL_MS = 3000;
// 3+ consecutive failed status polls → surface the trouble instead of spinning
// silently; the warning clears on the next successful poll.
const POLL_FAILURE_WARNING_THRESHOLD = 3;
// The inline viewer opens fit to width with the thumbnail pane closed —
// Chrome's default (fit-page beside thumbnails) shows the page at ~32%.
const PDF_VIEW = '#view=FitH&navpanes=0';

// A picker option (sc-6439): two lines and the text its matching reads.
type SubjectOption =
  | { kind: 'district'; key: string; line1: string; line2: string; search: string; district: SyncDistrictSubject }
  | { kind: 'branch'; key: string; line1: string; line2: string; search: string; branch: SyncBranchSubject };

const typedWords = (inputValue: string) => inputValue.trim().toLowerCase().split(/\s+/).filter(Boolean);

// Every typed word must appear in an option's search text. A district reads
// its name and its branch labels; a branch its label, Edge name and district,
// and a matching branch brings its district along. Districts stay first.
export function filterSubjects(options: SubjectOption[], inputValue: string): SubjectOption[] {
  const words = typedWords(inputValue);
  if (words.length === 0) return options;
  const matches = (option: SubjectOption) => words.every((word) => option.search.includes(word));
  const branches = options.filter((option) => option.kind === 'branch' && matches(option));
  const branchDistricts = new Set(
    branches.flatMap((option) =>
      option.kind === 'branch' && option.branch.crm_org_unit_id ? [option.branch.crm_org_unit_id] : []
    )
  );
  const districts = options.filter(
    (option) =>
      option.kind === 'district' && (matches(option) || branchDistricts.has(option.district.crm_org_unit_id))
  );
  return [...districts, ...branches];
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Matched text in bold — plain text otherwise, never a chip.
function highlight(text: string, inputValue: string): React.ReactNode {
  const words = typedWords(inputValue);
  if (words.length === 0 || !text) return text;
  const pattern = new RegExp(`(${words.map(escapeRegExp).join('|')})`, 'gi');
  return text.split(pattern).map((part, i) => (i % 2 === 1 ? <b key={i}>{part}</b> : part));
}

// Run status in plain text (the list's chip is gone, sc-6439); blue/orange,
// never red/green — so not primary.main, which is the app theme's green.
const statusTextColor = (status: SyncDocumentSummary['status']) =>
  status === 'failed' ? 'warning.main' : status === 'completed' ? 'text.secondary' : 'info.main';

// The PDF request is responseType:'blob', so on error the server's JSON {error}
// arrives as a Blob — read it back before falling through to the generic message.
async function readBlobError(e: unknown, fallback: string): Promise<string> {
  const data = (e as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.error ?? fallback;
    } catch {
      return fallback;
    }
  }
  return extractApiError(e, fallback);
}

export function SyncGeneratorPage({ pollIntervalMs = POLL_INTERVAL_MS }: { pollIntervalMs?: number } = {}) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const locationParam = searchParams.get('location');
  const districtParam = searchParams.get('district');
  const docParam = searchParams.get('doc');
  const activeDocId = docParam && /^\d+$/.test(docParam) ? Number(docParam) : null;
  const [repContext, setRepContext] = useState('');
  const [tab, setTab] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [reviseError, setReviseError] = useState<string | null>(null);

  const setParams = (next: { location?: number | null; district?: string | null; doc?: number | null }) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if ('location' in next) {
          if (next.location == null) params.delete('location');
          else params.set('location', String(next.location));
        }
        if ('district' in next) {
          if (next.district == null) params.delete('district');
          else params.set('district', next.district);
        }
        if ('doc' in next) {
          if (next.doc == null) params.delete('doc');
          else params.set('doc', String(next.doc));
        }
        return params;
      },
      { replace: true }
    );
  };

  // The picker's districts and branches (sc-6439). HEXA's own yards aren't
  // branches — the server leaves them out, and refuses them on create too.
  const { data: subjects, isLoading: subjectsLoading } = useQuery({
    queryKey: ['sync-generator-subjects'],
    queryFn: () => syncDocumentsApi.subjects(),
  });

  const subjectOptions = useMemo<SubjectOption[]>(() => {
    if (!subjects) return [];
    return [
      ...subjects.districts.map(
        (district): SubjectOption => ({
          kind: 'district',
          key: `district-${district.crm_org_unit_id}`,
          line1: `${district.name} · ${district.company}`,
          line2: district.labels.join(', '),
          search: [district.name, ...district.labels].join(' ').toLowerCase(),
          district,
        })
      ),
      ...subjects.branches.map(
        (branch): SubjectOption => ({
          kind: 'branch',
          key: `branch-${branch.location_id}`,
          line1: [branch.label, branch.company].filter(Boolean).join(' · '),
          line2: branch.district ?? '',
          search: [branch.label, branch.name, branch.district ?? ''].join(' ').toLowerCase(),
          branch,
        })
      ),
    ];
  }, [subjects]);

  // The picked subject is resolved from the URL once the subjects land; an
  // unknown id resolves to nothing (the empty picker).
  const subject = useMemo<SubjectOption | null>(() => {
    if (districtParam != null) {
      return subjectOptions.find((o) => o.kind === 'district' && o.district.crm_org_unit_id === districtParam) ?? null;
    }
    if (locationParam != null) {
      return subjectOptions.find((o) => o.kind === 'branch' && String(o.branch.location_id) === locationParam) ?? null;
    }
    return null;
  }, [subjectOptions, districtParam, locationParam]);
  const location = subject?.kind === 'branch' ? subject.branch : null;
  const district = subject?.kind === 'district' ? subject.district : null;
  const historyKey = district
    ? ['sync-documents-district', district.crm_org_unit_id]
    : ['sync-documents', location?.location_id];

  const { data: history = [] } = useQuery({
    queryKey: historyKey,
    queryFn: () =>
      district
        ? syncDocumentsApi.listForDistrict(district.crm_org_unit_id)
        : syncDocumentsApi.listForLocation(location!.location_id),
    enabled: !!subject,
  });

  // Recent syncs (sc-6437): only while the URL names no subject. Keyed off the
  // params, not the resolved subject, so a ?location= link doesn't flash the
  // recent list while the subjects are still loading.
  const noSubjectParam = locationParam == null && districtParam == null;
  const { data: recent = [] } = useQuery({
    queryKey: ['sync-documents-recent'],
    queryFn: () => syncDocumentsApi.listRecent(),
    enabled: noSubjectParam,
  });

  // Recent branch rows are named from the subjects' branches; a district row
  // carries its own name (the page header's). The card waits for the
  // subjects: the two load in parallel, and a recent list that lands first
  // would show `Location #<id>` on every row until names arrive.
  const locationNames = useMemo(
    () => new Map((subjects?.branches ?? []).map((b) => [b.location_id, b.name])),
    [subjects]
  );

  const { data: doc, dataUpdatedAt, errorUpdatedAt } = useQuery({
    queryKey: ['sync-document', activeDocId],
    queryFn: () => syncDocumentsApi.getOne(activeDocId!),
    // != null (not truthiness): a ?doc=0 URL must still fire the query — a
    // truthy check would leave awaitingFirstPoll true forever with the query
    // dead, freezing the form with no banner (nothing ever fails or succeeds).
    enabled: activeDocId != null,
    // The poll loop is its own retry mechanism — per-fetch retries would only
    // blur the consecutive-failed-poll count the warning banner is built on.
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'queued' || status === 'running') return pollIntervalMs;
      // No status has ever landed (cold reload with the backend unreachable,
      // or a stale ?doc=): keep polling until we learn the doc's actual state.
      // With retry: false a failed FIRST fetch would otherwise return false
      // here — and false clears the interval for good (react-query never
      // re-evaluates it without a fetch), leaving a dead page.
      if (query.state.data === undefined) return pollIntervalMs;
      return false;
    },
  });

  // Consecutive failed status polls. react-query's failureCount resets on every
  // fetch dispatch (it counts retries within one fetch, not dead polls in a
  // row), so count the error/success ticks ourselves.
  const [pollFailures, setPollFailures] = useState(0);
  useEffect(() => {
    if (errorUpdatedAt) setPollFailures((n) => n + 1);
  }, [errorUpdatedAt]);
  useEffect(() => {
    setPollFailures(0); // any successful poll (or switching docs) clears the streak
  }, [dataUpdatedAt, activeDocId]);

  // Per-document scratch state: a half-typed revision or a stale error must
  // not follow the rep onto the next document.
  useEffect(() => {
    setFeedback('');
    setPdfError(null);
    setReviseError(null);
  }, [activeDocId]);

  const generate = useMutation({
    mutationFn: () =>
      district
        ? syncDocumentsApi.createForDistrict(district.crm_org_unit_id, repContext.trim())
        : syncDocumentsApi.create(location!.location_id, repContext.trim()),
    onSuccess: (created: SyncDocument) => {
      setParams({ doc: created.id });
      setTab(0);
      queryClient.invalidateQueries({ queryKey: historyKey });
    },
  });

  // Revise (sc-6344): a child run on the same branch; the server replays this
  // run's transcript with the feedback. Success hands off to the existing
  // ?doc= running card + poll flow exactly like a fresh Generate.
  const revise = useMutation({
    mutationFn: () => syncDocumentsApi.revise(doc!.id, feedback.trim()),
    onSuccess: (child: SyncDocument) => {
      setParams({ doc: child.id });
      setTab(0);
      queryClient.invalidateQueries({ queryKey: historyKey });
    },
    onError: (e) => setReviseError(extractApiError(e, 'Failed to start the revision')),
  });

  // Download PDF (sc-6344): fetch the bytes (token-authenticated blob), then
  // trigger an object-URL download with the server filename.
  const downloadPdf = useMutation({
    mutationFn: (kind: SyncPdfKind) => syncDocumentsApi.pdf(doc!.id, kind),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Defer revoke: revoking synchronously right after click can cancel the
      // in-flight download on some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    },
    onError: async (e) => setPdfError(await readBlobError(e, 'PDF download failed')),
  });

  const running = doc?.status === 'queued' || doc?.status === 'running';
  // Between the create POST resolving and that run's first status fetch landing,
  // `doc` is undefined — so `running` alone would re-enable Generate in that
  // window, and a second click would start a second paid run. Gate on the
  // fetch-pending state too (also briefly true while loading a clicked past sync).
  const awaitingFirstPoll = activeDocId != null && !doc;

  // awaitingFirstPoll is included so a run whose FIRST poll can't reach the
  // server (cold reload, backend restart) surfaces the same warning instead
  // of an unexplained blank page — there's no running card yet to hang it on.
  const pollTrouble = (running || awaitingFirstPoll) && pollFailures >= POLL_FAILURE_WARNING_THRESHOLD;

  // The URL is shareable state, so a mis-pasted or hand-edited link can pair a
  // doc id with the wrong branch or district. Never render one subject's
  // document under another's label — refuse with a visible notice instead.
  // (The doc's own subject is authoritative: location_id and crm_org_unit_id
  // ride every show response.)
  const docMismatch =
    doc != null &&
    ((locationParam != null && String(doc.location_id) !== locationParam) ||
      (districtParam != null && (doc.crm_org_unit_id ?? null) !== districtParam));
  const isDistrictDoc = doc?.crm_org_unit_id != null;

  // A revision's lineage (sc-6344). Payloads from before the revision keys
  // shipped carry neither — `?? null` reads them as roots.
  const parentRunId = doc?.revised_from_id ?? null;
  const isRevision = parentRunId != null;

  const resetForAnotherSync = () => {
    setParams({ doc: null });
    setRepContext(''); // fresh run = context box again
    queryClient.invalidateQueries({ queryKey: historyKey });
  };

  const generateError = generate.error
    ? (generate.error as any)?.response?.data?.error ?? 'Failed to start the sync run'
    : null;

  // v0.4 (sc-6408): the one-page PDF, fetched once per document (a stored
  // page always renders the same) and shown inline from an object URL.
  const isOnePage = doc?.page != null;
  const pagePdf = useQuery({
    queryKey: ['sync-document-pdf', doc?.id],
    queryFn: async () => {
      try {
        return await syncDocumentsApi.pagePdf(doc!.id);
      } catch (e) {
        throw new Error(await readBlobError(e, 'Could not load the PDF'));
      }
    },
    enabled: doc?.status === 'completed' && isOnePage && !docMismatch,
    staleTime: Infinity,
    retry: false,
  });
  const [pagePdfUrl, setPagePdfUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!pagePdf.data) {
      setPagePdfUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(pagePdf.data.blob);
    setPagePdfUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pagePdf.data]);

  const downloadPagePdf = () => {
    if (!pagePdfUrl || !pagePdf.data) return;
    const link = document.createElement('a');
    link.href = pagePdfUrl;
    link.download = pagePdf.data.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const activeMarkdown = useMemo(() => {
    if (!doc || doc.status !== 'completed') return null;
    return tab === 0 ? doc.customer_doc : doc.talk_track;
  }, [doc, tab]);

  // Revise (sc-6344) sits under either view, under every completed doc — no
  // chain-length cap (sc-6437). Revising a pre-v0.4 document is refused
  // server-side (sc-6408) and the message shows under the field.
  const reviseBox = (
    <Box sx={{ p: 2 }} className="sync-no-print">
      <Stack spacing={1} alignItems="flex-start">
        <TextField
          fullWidth
          multiline
          minRows={2}
          placeholder="What should change?"
          inputProps={{ 'aria-label': 'What should change?' }}
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          disabled={running || awaitingFirstPoll || revise.isPending}
        />
        {reviseError && (
          <Alert severity="error" sx={{ alignSelf: 'stretch' }}>
            {reviseError}
          </Alert>
        )}
        <Button
          variant="outlined"
          disabled={!feedback.trim() || running || awaitingFirstPoll || revise.isPending}
          onClick={() => {
            setReviseError(null);
            revise.mutate();
          }}
        >
          Revise
        </Button>
      </Stack>
    </Box>
  );

  return (
    <Box>
      {/* Browser print stylesheet: only the rendered document survives printing. */}
      <style>{`
        @media print {
          .MuiAppBar-root, .MuiDrawer-root, .sync-no-print { display: none !important; }
          main { padding: 0 !important; margin: 0 !important; }
          .sync-print-area { border: none !important; box-shadow: none !important; padding: 0 !important; }
        }
      `}</style>

      <Box className="sync-no-print">
        <Typography variant="h4" gutterBottom>
          Sync Generator
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Generates SYNC prep for a branch or a district: a one-page customer PDF and notes for the rep.
          Every number is drawn from live Edge/HQ data — read the page and the notes before anything
          reaches a customer.
        </Typography>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack spacing={2}>
            <Autocomplete
              options={subjectOptions}
              loading={subjectsLoading}
              value={subject}
              onChange={(_e, value) => {
                // changing (or clearing) the subject cleans every param — a
                // stale ?doc= from another branch or district must not survive
                // the switch
                setParams({
                  location: value?.kind === 'branch' ? value.branch.location_id : null,
                  district: value?.kind === 'district' ? value.district.crm_org_unit_id : null,
                  doc: null,
                });
              }}
              groupBy={(option) => (option.kind === 'district' ? 'Districts' : 'Branches')}
              filterOptions={(options, state) => filterSubjects(options, state.inputValue)}
              getOptionLabel={(option) => option.line1}
              isOptionEqualToValue={(a, b) => a.key === b.key}
              renderOption={(props, option, state) => (
                <li {...props} key={option.key}>
                  <Box>
                    <Typography variant="body2">{highlight(option.line1, state.inputValue)}</Typography>
                    {option.line2 && (
                      <Typography variant="caption" color="text.secondary">
                        {highlight(option.line2, state.inputValue)}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Branch or district" placeholder="Pick a branch or a district" />
              )}
              // awaitingFirstPoll gates these two fields for the same reason it
              // gates Generate: a run is in flight before its first status lands
              // (a window the cold-start recovery can stretch to minutes), and a
              // subject switch here would drop the URL's ?doc= pointer to it. A
              // stuck ?doc= is escapable via the nav item (paramless link).
              disabled={running || awaitingFirstPoll}
            />
            <TextField
              label="Context for this sync (optional)"
              placeholder='e.g. "they care about tire covers", "DM will be in the room"'
              value={repContext}
              onChange={(e) => setRepContext(e.target.value)}
              multiline
              minRows={2}
              disabled={running || awaitingFirstPoll}
            />
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<AutoAwesomeIcon />}
                disabled={!subject || running || generate.isPending || awaitingFirstPoll}
                onClick={() => generate.mutate()}
              >
                Generate
              </Button>
              {doc && !running && (
                <Button variant="outlined" startIcon={<ReplayIcon />} onClick={resetForAnotherSync}>
                  Make another sync
                </Button>
              )}
            </Stack>
            {generateError && <Alert severity="error">{generateError}</Alert>}
          </Stack>
        </Paper>
      </Box>

      {pollTrouble && (
        <Alert severity="warning" sx={{ mb: 1 }} className="sync-no-print">
          Can't reach the server — still retrying.
        </Alert>
      )}

      {docMismatch && (
        <Alert severity="warning" sx={{ mb: 3 }} className="sync-no-print">
          This sync belongs to a different branch or district than the link says — check the link.
        </Alert>
      )}

      {parentRunId != null && !docMismatch && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} className="sync-no-print">
          Revision of run{' '}
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              setParams({ doc: parentRunId });
              setTab(0);
            }}
          >
            #{parentRunId}
          </Link>
        </Typography>
      )}

      {running && !docMismatch && (
        <Paper sx={{ p: 3, mb: 3 }} className="sync-no-print">
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <CircularProgress size={20} />
            <Typography>
              {doc?.status === 'queued'
                ? 'Queued…'
                : isRevision
                  ? 'Revising — usually under a minute'
                  : 'Generating — the agent is digging through the data…'}
            </Typography>
          </Stack>
          <LinearProgress />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {isRevision
              ? 'You can leave this page — the run continues and shows up under past syncs.'
              : 'Runs usually take a few minutes. You can leave this page — the run continues and shows up under past syncs.'}
          </Typography>
        </Paper>
      )}

      {doc?.status === 'failed' && !docMismatch && (
        <Alert severity="error" sx={{ mb: 3 }} className="sync-no-print">
          <Typography variant="subtitle2">This run failed</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {doc.error || 'No error recorded'}
          </Typography>
        </Alert>
      )}

      {doc?.status === 'completed' && !docMismatch && isOnePage && (
        <Paper sx={{ mb: 3 }}>
          <Stack direction="row" justifyContent="flex-end" sx={{ px: 2, pt: 1 }}>
            <Button startIcon={<DownloadIcon />} disabled={!pagePdfUrl} onClick={downloadPagePdf}>
              Download PDF
            </Button>
          </Stack>
          {pagePdf.data?.overflow && (
            <Alert severity="warning" sx={{ mx: 2, mb: 1 }}>
              Over the one-page limits: {pagePdf.data.overflow}
            </Alert>
          )}
          {pagePdf.error && (
            <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
              {(pagePdf.error as Error).message}
            </Alert>
          )}
          <Divider />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ p: 3 }}>
            <Box sx={{ flex: 3, minWidth: 0 }}>
              {pagePdfUrl ? (
                <Box
                  component="iframe"
                  title="SYNC page"
                  src={`${pagePdfUrl}${PDF_VIEW}`}
                  // a district page is portrait (sc-6439); a branch page landscape
                  sx={{
                    width: '100%',
                    aspectRatio: isDistrictDoc ? '8.5 / 11' : '11 / 8.5',
                    border: 1,
                    borderColor: 'divider',
                  }}
                />
              ) : (
                pagePdf.isLoading && <CircularProgress size={24} />
              )}
            </Box>
            <Box sx={{ flex: 2, minWidth: 0 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Notes for the rep — not on the page
              </Typography>
              <ReactMarkdown>{doc.notes ?? ''}</ReactMarkdown>
            </Box>
          </Stack>
          <Divider />
          {reviseBox}
        </Paper>
      )}

      {doc?.status === 'completed' && !docMismatch && !isOnePage && (
        <Paper sx={{ mb: 3 }} className="sync-print-area">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            className="sync-no-print"
            sx={{ px: 2, pt: 1 }}
          >
            <Tabs value={tab} onChange={(_e, value) => setTab(value)}>
              <Tab label="Customer doc" />
              <Tab label="Talk track" />
            </Tabs>
            <Stack direction="row" spacing={1}>
              <Button startIcon={<PrintIcon />} onClick={() => window.print()}>
                Print
              </Button>
              <Button
                startIcon={<DownloadIcon />}
                disabled={downloadPdf.isPending}
                onClick={() => {
                  setPdfError(null);
                  downloadPdf.mutate(tab === 0 ? 'customer' : 'talk');
                }}
              >
                Download PDF
              </Button>
            </Stack>
          </Stack>
          {pdfError && (
            <Alert severity="error" sx={{ mx: 2, mb: 1 }} className="sync-no-print">
              {pdfError}
            </Alert>
          )}
          <Divider className="sync-no-print" />
          <Box sx={{ p: 3, '& h1': { mt: 0 } }}>
            <ReactMarkdown>{activeMarkdown ?? ''}</ReactMarkdown>
          </Box>
          <Divider className="sync-no-print" />
          {reviseBox}
        </Paper>
      )}

      {subject && history.length > 0 && (
        <Paper sx={{ p: 2 }} className="sync-no-print">
          <Typography variant="h6" gutterBottom>
            Past syncs — {district ? district.name : location?.name}
          </Typography>
          <List dense>
            {history.map((row) => (
              <ListItemButton
                key={row.id}
                selected={row.id === activeDocId}
                onClick={() => {
                  setParams({ doc: row.id });
                  setTab(0);
                }}
              >
                <ListItemText
                  primary={new Date(row.created_at).toLocaleString()}
                  secondary={
                    row.revised_from_id != null
                      ? `Revision of #${row.revised_from_id} — ${row.feedback ?? ''}`
                      : row.rep_context || undefined
                  }
                />
                <Typography variant="body2" sx={{ color: statusTextColor(row.status), flexShrink: 0, ml: 2 }}>
                  {row.status}
                </Typography>
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      {noSubjectParam && !subjectsLoading && recent.length > 0 && (
        <Paper sx={{ p: 2 }} className="sync-no-print">
          <Typography variant="h6" gutterBottom>
            Recent syncs
          </Typography>
          <List dense>
            {recent.map((row) => {
              const districtRow = row.crm_org_unit_id != null;
              return (
                <ListItemButton
                  key={row.id}
                  onClick={() => {
                    setParams(
                      districtRow
                        ? { district: row.crm_org_unit_id, location: null, doc: row.id }
                        : { location: row.location_id, district: null, doc: row.id }
                    );
                    setTab(0);
                  }}
                >
                  {/* Subject, then time · creator only — no lineage or rep context: revision
                      numbers mean nothing outside the subject's view, and a rep's prompts
                      aren't for everyone's list. A district row names its district from
                      its page header and says "District" first. */}
                  <ListItemText
                    primary={
                      districtRow
                        ? row.district_name ?? 'District'
                        : (row.location_id != null && locationNames.get(row.location_id)) || `Location #${row.location_id}`
                    }
                    secondary={[
                      districtRow ? 'District' : null,
                      new Date(row.created_at).toLocaleString(),
                      row.user?.name || row.user?.email,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Paper>
      )}
    </Box>
  );
}
